import React from 'react';
import { Col, List, Row, Tag, Typography, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { ControlTowerQueryParams } from '@/services/manufacturingControlTower.service';
import { useControlTowerAttentionQueue } from '@/hooks/useManufacturingControlTower';

const { Text } = Typography;

interface QueueItem {
  work_order_id: string;
  work_order_number: string;
  customer_name?: string | null;
  reason?: string;
  deepLink?: string;
  next_operation?: string | null;
}

interface Props {
  filters?: ControlTowerQueryParams;
  refreshInterval?: number;
}

const QueueList: React.FC<{
  items: QueueItem[];
  empty: string;
  onOpen: (item: QueueItem) => void;
}> = ({ items, empty, onOpen }) => {
  if (!items.length) return <EmptyQueueState message={empty} />;
  return (
    <List
      size="small"
      dataSource={items}
      renderItem={(item) => (
        <List.Item style={{ cursor: 'pointer', padding: '4px 0' }} onClick={() => onOpen(item)}>
          <SpaceLine
            number={item.work_order_number}
            reason={item.reason}
            next={item.next_operation}
            customer={item.customer_name}
          />
        </List.Item>
      )}
    />
  );
};

const SpaceLine: React.FC<{
  number: string;
  reason?: string;
  next?: string | null;
  customer?: string | null;
}> = ({ number, reason, next, customer }) => (
  <div>
    <Text strong>{number}</Text>
    {customer ? <Text type="secondary"> · {customer}</Text> : null}
    {next ? (
      <div>
        <Tag>{next}</Tag>
      </div>
    ) : null}
    {reason ? (
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {reason}
        </Text>
      </div>
    ) : null}
  </div>
);

export const AttentionQueueZone: React.FC<Props> = ({ filters, refreshInterval }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useControlTowerAttentionQueue(filters, refreshInterval);
  const q = data as {
    delayed?: QueueItem[];
    blocked?: QueueItem[];
    can_start?: QueueItem[];
    inspection_waiting?: QueueItem[];
  };

  const open = (item: QueueItem) => {
    navigate(item.deepLink || `/mfg/work-orders/${item.work_order_id}`);
  };

  if (isLoading) {
    return (
      <ControlTowerSectionCard zone="myTasks" sectionId="my-queue" title="My attention queue">
        <Spin />
      </ControlTowerSectionCard>
    );
  }

  const delayed = q?.delayed ?? [];
  const blocked = q?.blocked ?? [];
  const canStart = q?.can_start ?? [];
  const inspection = q?.inspection_waiting ?? [];
  const total = delayed.length + blocked.length + canStart.length + inspection.length;

  return (
    <ControlTowerSectionCard
      zone="myTasks"
      sectionId="my-queue"
      title={`My attention queue (${total})`}
      style={{ marginBottom: 16 }}
      extra={
        <Text
          type="secondary"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/mfg/work-order-operations')}
        >
          Execution board
        </Text>
      }
    >
      {!total ? (
        <EmptyQueueState
          message="Nothing needing your attention right now."
          actionLabel="Open factory floor"
          onAction={() => navigate('/mfg/factory-floor')}
        />
      ) : (
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} xl={6}>
            <ControlTowerSubQueuePanel zone="overdue" title={`Delayed`} count={delayed.length}>
              <QueueList items={delayed} empty="No delayed WOs" onOpen={open} />
            </ControlTowerSubQueuePanel>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <ControlTowerSubQueuePanel zone="ordersAtRisk" title={`Blocked`} count={blocked.length}>
              <QueueList items={blocked} empty="No blocked stages" onOpen={open} />
            </ControlTowerSubQueuePanel>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <ControlTowerSubQueuePanel zone="pendingApprovals" title={`Can start`} count={canStart.length}>
              <QueueList items={canStart} empty="Nothing ready to start" onOpen={open} />
            </ControlTowerSubQueuePanel>
          </Col>
          <Col xs={24} md={12} xl={6}>
            <ControlTowerSubQueuePanel
              zone="qmsGateIir"
              title={`Inspection waiting`}
              count={inspection.length}
            >
              <QueueList items={inspection} empty="No inspection waits" onOpen={open} />
            </ControlTowerSubQueuePanel>
          </Col>
        </Row>
      )}
    </ControlTowerSectionCard>
  );
};

export default AttentionQueueZone;
